import { SkyReplacementOption } from 'models/purchaseFlow/AIEditingDTO'
import { useCallback } from 'react'
import { logger } from 'utils/logging'
import { PhotoStudioGenerationStepId } from '../generationSteps'
import { useAIEnhancementProcessor } from './useAIEnhancementProcessor.hook'

interface UseAIEnhancementFlowProps {
  assignmentId: string
  sourceVisualFilename: string
  getLatestPreviewFilename: () => string | null
  onProgressUpdate?: (step: string) => void
  onComplete?: (resultUrl: string) => void
  onError?: (error: any) => void
}

/**
 * Complete AI Enhancement flow hook
 * Handles processing and result retrieval
 *
 * Usage:
 * 1. Call processAIEnhancement with optional skyReplacement
 * 2. Hook handles processing, polling, and result retrieval
 * 3. Calls onComplete with result URL when finished
 */
export const useAIEnhancementFlow = ({
  assignmentId,
  sourceVisualFilename,
  getLatestPreviewFilename,
  onProgressUpdate,
  onComplete,
  onError
}: UseAIEnhancementFlowProps) => {
  const { processEnhancement, isProcessing, error } = useAIEnhancementProcessor({
    assignmentId,
    sourceVisualFilename,
    getLatestPreviewFilename,
    onProgressUpdate,
    onComplete,
    onError
  })

  const processAIEnhancement = useCallback(async (
    skyReplacement?: SkyReplacementOption | null
  ): Promise<string> => {
    try {
      onProgressUpdate?.(PhotoStudioGenerationStepId.START_PROCESSING)

      const result = await processEnhancement(skyReplacement)

      if (result.success) {
        return result.jobId!
      } else {
        throw result.error
      }
    } catch (error) {
      logger.error('AI Enhancement: Flow failed', error, {
        context: {
          component: 'PhotoStudio',
          action: 'aiEnhancementFlow',
          metadata: { assignmentId }
        }
      })
      onError?.(error)
      throw error
    }
  }, [processEnhancement, onProgressUpdate, onError, assignmentId])

  return {
    processAIEnhancement,
    isProcessing,
    error
  }
}
